// Незавершённая попытка выплаты по финансовой записи.
// payoutUuid создаётся один раз на попытку; пока исход неизвестен (таймаут, сеть, 5xx),
// повторяем запрос с тем же uuid и тем же телом. Попытка хранится в sessionStorage,
// чтобы пережить закрытие модалки и перезагрузку страницы.

export interface PayoutDraft {
  amountKopecks: number;
  note: string;
}

export interface PayoutAttempt {
  payoutUuid: string;
  draft: PayoutDraft;
}

export type PayoutOutcome = 'success' | 'rejected' | 'alreadyRecorded' | 'unknown';

const KEY_PREFIX = 'epl.payoutAttempt.';
// Код из errors[0].code ответа 409
const CONFLICT_CODE = 'payout_uuid_conflict';

function isAttempt(value: unknown): value is PayoutAttempt {
  const attempt = value as PayoutAttempt;
  return (
    typeof attempt?.payoutUuid === 'string' &&
    Number.isSafeInteger(attempt.draft?.amountKopecks) &&
    typeof attempt.draft?.note === 'string'
  );
}

export function newPayoutUuid(): string {
  return crypto.randomUUID();
}

export function getPayoutAttempt(recordId: string): PayoutAttempt | null {
  try {
    const raw = sessionStorage.getItem(KEY_PREFIX + recordId);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isAttempt(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function savePayoutAttempt(recordId: string, attempt: PayoutAttempt): void {
  try {
    sessionStorage.setItem(KEY_PREFIX + recordId, JSON.stringify(attempt));
  } catch {
    // sessionStorage недоступен: попытка живёт только до закрытия модалки
  }
}

export function clearPayoutAttempt(recordId: string): void {
  try {
    sessionStorage.removeItem(KEY_PREFIX + recordId);
  } catch {
    // нечего очищать
  }
}

// Убирает попытки, выплаты по которым уже видны в истории операций.
export function clearSettledPayoutAttempts(settledPayoutUuids: Iterable<string>): void {
  const settled = new Set(settledPayoutUuids);
  try {
    const keys = Object.keys(sessionStorage).filter((key) => key.startsWith(KEY_PREFIX));
    keys.forEach((key) => {
      const attempt = getPayoutAttempt(key.slice(KEY_PREFIX.length));
      if (attempt && settled.has(attempt.payoutUuid)) sessionStorage.removeItem(key);
    });
  } catch {
    // sessionStorage недоступен
  }
}

// 2xx и 4xx — исход определён; сеть, таймаут и 5xx — неизвестен.
// 409 payout_uuid_conflict значит, что выплата с этим uuid уже записана на сервере:
// попытку очищаем и обновляем данные, новый uuid не создаём.
export function classifyPayoutOutcome(status: number | undefined, errorCode?: string): PayoutOutcome {
  if (status === undefined || status >= 500) return 'unknown';
  if (status >= 200 && status < 300) return 'success';
  if (status === 409 && errorCode === CONFLICT_CODE) return 'alreadyRecorded';
  if (status >= 400) return 'rejected';
  return 'unknown';
}

// Что делать с попыткой после ответа:
// success и alreadyRecorded — выплата записана, попытку убираем и обновляем данные;
// rejected — ничего не записано, попытку убираем, после правки будет новый uuid;
// unknown — попытка остаётся, поля закреплены, предлагаем только «Повторить».
export function resolvePayoutOutcome(outcome: PayoutOutcome): {
  clearAttempt: boolean;
  next: 'done' | 'already' | 'edit' | 'retry';
} {
  switch (outcome) {
    case 'success':
      return { clearAttempt: true, next: 'done' };
    case 'alreadyRecorded':
      return { clearAttempt: true, next: 'already' };
    case 'rejected':
      return { clearAttempt: true, next: 'edit' };
    case 'unknown':
      return { clearAttempt: false, next: 'retry' };
  }
}

export interface PayoutResult<T> {
  outcome: PayoutOutcome;
  next: 'done' | 'already' | 'edit' | 'retry';
  response?: T;
  error?: unknown;
}

// Попытки выплаты по одной записи. Попытка хранится и в памяти, и в sessionStorage:
// если хранилище недоступно, «Повторить» всё равно отправит тот же uuid и то же тело.
export function createPayoutSession<T>(recordId: string, send: (attempt: PayoutAttempt) => Promise<T>) {
  let current: PayoutAttempt | null = getPayoutAttempt(recordId);

  const run = async (attempt: PayoutAttempt): Promise<PayoutResult<T>> => {
    let result: PayoutResult<T>;
    try {
      const response = await send(attempt);
      result = { outcome: 'success', next: 'done', response };
    } catch (error) {
      const { status, code } = (error ?? {}) as { status?: number; code?: string };
      const outcome = classifyPayoutOutcome(status, code);
      result = { outcome, next: resolvePayoutOutcome(outcome).next, error };
    }

    if (resolvePayoutOutcome(result.outcome).clearAttempt) {
      current = null;
      clearPayoutAttempt(recordId);
    }
    return result;
  };

  return {
    // Незавершённая попытка: из хранилища, если оно есть, иначе из памяти
    pending(): PayoutAttempt | null {
      return getPayoutAttempt(recordId) ?? current;
    },

    submit(draft: PayoutDraft): Promise<PayoutResult<T>> {
      const attempt: PayoutAttempt = { payoutUuid: newPayoutUuid(), draft };
      current = attempt;
      savePayoutAttempt(recordId, attempt);
      return run(attempt);
    },

    // Повтор незавершённой попытки с тем же uuid и телом. null, если повторять нечего.
    retry(): Promise<PayoutResult<T>> | null {
      const attempt = getPayoutAttempt(recordId) ?? current;
      return attempt ? run(attempt) : null;
    },
  };
}
